// NeuAnlageKnopf.tsx — das „+“ im App-Header samt Menü und Vorfrage-Dialogen.
//
// Verdrahtet die acht Einträge mit den vorhandenen Anlegewegen:
//  - Kontakt  → /contacts/new (fragt Status und Firma/Privat selbst)
//  - Risiko   → Vorfrage Kontakt + Risikoart  → openRiskFormWindow
//  - Vertrag  → Vorfrage Kontakt + Vertragsart → KFZ-Fenster, /vertrag/transport,
//               /vertrag/new (Allgemein/Ertragsausfall) oder Immobilien-Fenster
//  - Schaden  → Vorfrage Kontakt              → openSchadenFormWindow
//  - Vorgang  → openVorgangCreateFlow (fragt Kontakt und Art selbst)
//  - Termin   → openTerminFormWindow (Termin-Dialog mit eigenem Kontaktfeld)
//  - Mail     → openComposeWindow (Empfänger im Verfassen-Fenster)
//  - Dokument → Vorfrage Art + Kontakt + Vorgang? → Word/Excel:
//               documentService.createOffice + Editor-Tab; Datei hochladen:
//               documentService.upload
//
// Die Vorfragen starten mit dem, was die aktuelle Seite weiß (T-230): im
// Vorgang Kunde + Vorgang, in der Kontaktakte der Kontakt (vorbelegung.ts).

import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openRiskFormWindow } from "../windowManager/RiskFormWindow";
import { openContractFormWindow } from "../windowManager/ContractFormWindow";
import { openSchadenFormWindow } from "../windowManager/SchadenFormWindow";
import { openTerminFormWindow } from "../windowManager/openTerminFormWindow";
import { openVorgangCreateFlow } from "../vorgang/create/VorgangCreateFlowWindow";
import { openComposeWindow } from "../mail/ComposeMailWindow";
import { documentService } from "../../services/documentService";
import { vorgangService } from "../../services/vorgangService";
import { apiUrl } from "../../utils/api";
import { NeuMenue } from "./NeuMenue";
import {
  NeuAnlageDialog,
  type ImmobilienRisiko,
  type NeuAnlageAktionen,
  type NeuAnlageVorbelegung,
  type VorfrageArt,
  type VorgangAuswahl,
  vorgaengeFuerAuswahl,
} from "./NeuAnlageDialog";
import { kontaktTrefferAus, seitenKontext, vorbelegungErmitteln, type RohKontakt } from "./vorbelegung";
import { editorOeffnen, editorTabVerwerfen, editorTabVorbereiten, useOfficeVerfuegbar } from "./officeDokument";
import type { KontaktTreffer } from "./KontaktSuche";
import { MIT_VORFRAGE, sichtbareEintraege, type NeuAnlageArt } from "./neuAnlageEintraege";

type RohRisiko = {
  id: number;
  name?: string | null;
  riskType?: string | null;
  characteristics?: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }>;
};

const kontaktSuchen = async (text: string, signal: AbortSignal): Promise<KontaktTreffer[]> => {
  const res = await fetch(apiUrl("contact", { search: text, limit: 50 }), { signal });
  if (!res.ok) throw new Error(`Kontaktsuche: ${res.status}`);
  const daten = (await res.json()) as unknown;
  const liste = Array.isArray(daten) ? (daten as RohKontakt[]) : [];
  return liste
    .filter((c) => Number(c.id) > 0)
    .slice(0, 12)
    .map(kontaktTrefferAus);
};

const kontaktLaden = async (kontaktId: number): Promise<RohKontakt> => {
  const res = await fetch(apiUrl(`contact/${kontaktId}`));
  if (!res.ok) throw new Error(`Kontakt: ${res.status}`);
  return (await res.json()) as RohKontakt;
};

const immobilienRisikenLaden = async (kontaktId: number): Promise<ImmobilienRisiko[]> => {
  const res = await fetch(apiUrl(`risk-objects/by-contact/${kontaktId}`));
  if (!res.ok) return [];
  const daten = (await res.json()) as unknown;
  const liste = Array.isArray(daten) ? (daten as RohRisiko[]) : [];
  const wert = (r: RohRisiko, k: string) =>
    String((r.characteristics ?? []).find((c) => c.nameBezeichnung === k)?.riskCharacteristicValue ?? "").trim();
  // Gleiche Auswahl wie NewContractDialog: riskType enthält „immo".
  return liste
    .filter((r) => (r.riskType ?? "").toLowerCase().includes("immo"))
    .map((r) => ({
      id: r.id,
      name: r.name?.trim() || `Immobilie #${r.id}`,
      adresse: [wert(r, "street"), [wert(r, "zip"), wert(r, "city")].filter(Boolean).join(" ")].filter(Boolean).join(", ") || null,
    }));
};

const vorgaengeLaden = async (kontaktId: number): Promise<VorgangAuswahl[]> =>
  vorgaengeFuerAuswahl(await vorgangService.getVorgaengeByContact(kontaktId));

export function NeuAnlageKnopf({
  isPhone,
  knopfStyle,
  mailbox,
  onNavigiert,
}: {
  isPhone?: boolean;
  knopfStyle?: React.CSSProperties;
  /** Absender-Postfach für „Mail" — wie „Neue E-Mail" in der Sidebar. */
  mailbox?: string | null;
  /** Nach einer Navigation, z. B. um die Handy-Schublade zu schließen. */
  onNavigiert?: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasPermission } = useAuth();
  const { openWindow } = useWindowManager();
  const [vorfrage, setVorfrage] = React.useState<VorfrageArt | null>(null);
  const [vorbelegung, setVorbelegung] = React.useState<NeuAnlageVorbelegung | null>(null);
  // Nur die jüngste Wahl öffnet ihren Dialog, falls eine ältere noch lädt.
  const anfrage = React.useRef(0);
  const officeVerfuegbar = useOfficeVerfuegbar();

  const eintraege = React.useMemo(() => sichtbareEintraege(hasPermission), [hasPermission]);

  const gehe = React.useCallback(
    (pfad: string, state?: Record<string, unknown>) => {
      navigate(pfad, state ? { state } : undefined);
      onNavigiert?.();
    },
    [navigate, onNavigiert],
  );

  const aktionen = React.useMemo<NeuAnlageAktionen>(
    () => ({
      kontaktSuchen,
      immobilienRisikenLaden,
      risikoAnlegen: ({ riskType, kontakt }) =>
        openRiskFormWindow(openWindow, {
          riskType,
          contactId: kontakt.id,
          contactName: kontakt.name,
          initialSize: { width: 1080, height: 720 },
        }),
      vertragAnlegen: ({ ziel, kontakt }) => {
        if (ziel.typ === "kfz-fenster") {
          openContractFormWindow(openWindow, { variant: "kfz", contactId: kontakt.id, contactName: kontakt.name });
          return;
        }
        gehe(ziel.pfad, ziel.state);
      },
      immobilienVertragAnlegen: ({ kontakt, riskObjectId }) =>
        openContractFormWindow(openWindow, {
          variant: "immobilie",
          contactId: kontakt.id,
          contactName: kontakt.name,
          riskObjectId,
        }),
      schadenAnlegen: ({ kontakt }) =>
        openSchadenFormWindow(openWindow, { contactId: kontakt.id, contactName: kontakt.name }),
      dokumentHochladen: async ({ datei, kontaktId, kategorie, vorgangId }) => {
        const dok = await documentService.upload({
          file: datei,
          contactId: kontaktId,
          category: kategorie ?? undefined,
          ...(vorgangId != null ? { vorgangId } : {}),
        });
        return dok.id;
      },
      vorgaengeLaden,
      officeAnlegen: async ({ art, name, kontaktId, vorgangId }) => {
        const dok = await documentService.createOffice({ art, name, contactId: kontaktId, vorgangId });
        return dok.id;
      },
      editorTabVorbereiten,
      editorOeffnen,
      editorTabVerwerfen,
      kontaktOeffnen: (kontaktId, dokumentId) =>
        gehe(`/contacts/${kontaktId}?tab=Dokumente${dokumentId ? `&doc=${dokumentId}` : ""}`),
    }),
    [openWindow, gehe],
  );

  const waehlen = (art: NeuAnlageArt) => {
    if (MIT_VORFRAGE.has(art)) {
      const nr = ++anfrage.current;
      const oeffnen = (vb: NeuAnlageVorbelegung | null) => {
        if (nr !== anfrage.current) return;
        setVorbelegung(vb);
        setVorfrage(art as VorfrageArt);
      };
      const kontext = seitenKontext(pathname);
      if (!kontext) {
        oeffnen(null);
        return;
      }
      // Der Vorgang liegt meist schon im Cache (die Seite hat ihn eben geladen);
      // ohne Antwort oder bei Fehler öffnet der Dialog wie bisher leer.
      void vorbelegungErmitteln(kontext, {
        vorgangLaden: (id) => vorgangService.getVorgang(id),
        kontaktLaden,
      }).then(oeffnen);
      return;
    }
    switch (art) {
      case "kontakt":
        gehe("/contacts/new");
        return;
      case "vorgang":
        openVorgangCreateFlow(openWindow, {});
        return;
      case "termin":
        openTerminFormWindow(openWindow, {});
        return;
      case "mail":
        openComposeWindow(openWindow, mailbox ? { mailbox } : {});
        return;
    }
  };

  return (
    <>
      <NeuMenue eintraege={eintraege} onWaehlen={waehlen} isPhone={isPhone} knopfStyle={knopfStyle} />
      {vorfrage && (
        <NeuAnlageDialog
          art={vorfrage}
          aktionen={aktionen}
          onClose={() => {
            setVorfrage(null);
            setVorbelegung(null);
          }}
          vorbelegung={vorbelegung}
          // Solange die Antwort aussteht, zählt der Editor als vorhanden — sie
          // kommt aus einem gemerkten Aufruf und steht praktisch immer schon.
          officeVerfuegbar={officeVerfuegbar !== false}
        />
      )}
    </>
  );
}

export default NeuAnlageKnopf;
